import {
  estimateCouncilTaxMonthly,
  estimateUtilities,
} from "./costEstimates";

export interface AffordabilityInputs {
  price: number;
  rentFrequency: string;
  deposit: number | null;
  householdIncomeMonthly: number;
  occupants?: number;
  includeBills?: boolean;
  postcode?: string;
  bedrooms?: number | null;
  internalArea?: number | null;
  description?: string[];
  includeTvLicence?: boolean;
  includeContentsInsurance?: boolean;
}

export interface AffordabilityResult {
  monthlyRent: number;
  deposit: number;
  depositIsEstimate: boolean;
  moveInCost: number;
  councilTaxMonthly: number;
  utilitiesMonthly: number;
  broadbandMonthly: number;
  tvLicenceMonthly: number;
  contentsInsuranceMonthly: number;
  monthlyBills: number;
  monthlyTotal: number;
  affordabilityRatio: number | null;
  affordabilityLevel: "ok" | "amber" | "red" | "unknown";
}

const BROADBAND_MONTHLY = 48;

export function monthlyRentPcm(
  price: number,
  rentFrequency: string
): number {
  const freq = rentFrequency.trim().toLowerCase();
  if (freq === "weekly" || freq === "pw" || freq === "per week") {
    return Math.round((price * 52) / 12);
  }
  return price;
}

export function computeAffordability(
  inputs: AffordabilityInputs
): AffordabilityResult {
  const monthlyRent = monthlyRentPcm(inputs.price, inputs.rentFrequency);
  const weeklyRent = (monthlyRent * 12) / 52;
  const depositIsEstimate = inputs.deposit == null || inputs.deposit <= 0;
  const deposit = depositIsEstimate
    ? Math.round(weeklyRent * 5)
    : inputs.deposit!;

  const councilTaxMonthly = estimateCouncilTaxMonthly(
    inputs.postcode ?? "",
    inputs.description ?? [],
    inputs.occupants ?? 1
  );
  const utilitiesMonthly = estimateUtilities(
    inputs.bedrooms ?? 1,
    inputs.internalArea ?? null
  );
  const broadbandMonthly = inputs.includeBills === false ? 0 : BROADBAND_MONTHLY;
  const tvLicenceMonthly = inputs.includeTvLicence ? 14.95 : 0;
  const contentsInsuranceMonthly = inputs.includeContentsInsurance ? 15 : 0;

  const monthlyBills =
    inputs.includeBills === false
      ? 0
      : councilTaxMonthly +
        utilitiesMonthly +
        broadbandMonthly +
        tvLicenceMonthly +
        contentsInsuranceMonthly;

  const monthlyTotal = monthlyRent + monthlyBills;
  const moveInCost = deposit + monthlyRent;

  const income = inputs.householdIncomeMonthly;
  const affordabilityRatio =
    income > 0 ? monthlyTotal / income : null;

  let affordabilityLevel: AffordabilityResult["affordabilityLevel"] =
    "unknown";
  if (affordabilityRatio != null) {
    if (affordabilityRatio > 0.4) affordabilityLevel = "red";
    else if (affordabilityRatio > 0.3) affordabilityLevel = "amber";
    else affordabilityLevel = "ok";
  }

  return {
    monthlyRent,
    deposit,
    depositIsEstimate,
    moveInCost,
    councilTaxMonthly,
    utilitiesMonthly,
    broadbandMonthly,
    tvLicenceMonthly,
    contentsInsuranceMonthly,
    monthlyBills,
    monthlyTotal,
    affordabilityRatio,
    affordabilityLevel,
  };
}
